import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import AppModal from './AppModal';
import { messageFromError } from '../utils/apiError';   // Block 233

// K4 — household polls, surfaced on the Meal Planner. Vote, live tally, close,
// vote on a no-login member's behalf, and drop a winner into a meal-plan slot.

const FONT = "'Raleway', sans-serif";
const NAVY = '#1a2e3b';
const SLOTS = ['breakfast', 'lunch', 'dinner', 'snacks'];

export default function HouseholdPolls() {
  const { currentUser } = useCurrentUser();
  const isChild = currentUser?.isChildAccount === true;

  const [polls, setPolls] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [votingFor, setVotingFor] = useState({}); // pollId -> memberId | '' (me)
  const [planFor, setPlanFor] = useState(null);    // poll pending a slot placement
  const [planDate, setPlanDate] = useState('');
  const [planSlot, setPlanSlot] = useState('dinner');

  // Create modal
  const [creating, setCreating] = useState(false);
  const [newQ, setNewQ] = useState('');
  const [newOpts, setNewOpts] = useState(['', '']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([api('/api/household-polls'), api('/api/household').catch(() => null)]);
      const pData = await pRes.json();
      setPolls(pData.polls || []);
      if (hRes && hRes.ok) { const h = await hRes.json(); setRoster(h.members || []); }
    } catch (err) { setErr(messageFromError(err, 'Could not load your polls.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Members without a login an adult may vote for.
  const proxyMembers = roster.filter(m => m.inviteStatus === 'profile_only');

  async function vote(poll, optionId) {
    const memberId = votingFor[poll.id] || '';
    try {
      const res = await api(`/api/household-polls/${poll.id}/vote`, {
        method: 'POST',
        body: JSON.stringify({ optionId, ...(memberId ? { householdMemberId: memberId } : {}) }),
      });
      const data = await res.json();
      setPolls(prev => prev.map(p => p.id === poll.id ? data.poll : p));
    } catch (e) { setErr(messageFromError(e, 'That did not work. Please try again.')); }
  }

  async function closePoll(poll) {
    try {
      const res = await api(`/api/household-polls/${poll.id}/close`, { method: 'POST' });
      const data = await res.json();
      setPolls(prev => prev.map(p => p.id === poll.id ? data.poll : p));
    } catch (err) { setErr(messageFromError(err, 'Could not close that poll.')); }
  }

  async function deletePoll(poll) {
    setPolls(prev => prev.filter(p => p.id !== poll.id));
    try { await api(`/api/household-polls/${poll.id}`, { method: 'DELETE' }); }
    catch (err) { setErr(messageFromError(err, 'Could not delete that poll.')); load(); }
  }

  async function addWinnerToPlan() {
    if (!planFor || !planDate) return;
    try {
      const res = await api(`/api/household-polls/${planFor.id}/plan`, { method: 'POST', body: JSON.stringify({ date: planDate, slot: planSlot }) });
      setPlanFor(null); setPlanDate('');
      window.dispatchEvent(new CustomEvent('aidey:mealPlanUpdated'));
    } catch (e) { setErr(messageFromError(e, 'That did not work. Please try again.')); }
  }

  async function createPoll() {
    const options = newOpts.map(o => o.trim()).filter(Boolean).map(label => ({ label }));
    if (options.length < 2) { setErr('Add at least two options.'); return; }
    try {
      const res = await api('/api/household-polls', { method: 'POST', body: JSON.stringify({ kind: 'meal_choice', question: newQ.trim(), options }) });
      const data = await res.json();
      setPolls(prev => [data.poll, ...prev]);
      setCreating(false); setNewQ(''); setNewOpts(['', '']);
    } catch (err) { setErr(messageFromError(err, 'Could not create that poll.')); }
  }

  const open = polls.filter(p => p.status === 'open');
  const closed = polls.filter(p => p.status === 'closed');

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '1rem 1.1rem', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>🗳 Family polls</h3>
        <button onClick={() => setCreating(true)} style={{ fontFamily: FONT, fontSize: '0.82rem', fontWeight: 700, padding: '0.35rem 0.8rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer' }}>+ New poll</button>
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{err}</div>}

      {loading ? <p style={{ color: '#8a99a8', fontSize: '0.85rem' }}>Loading…</p> : (open.length === 0 && closed.length === 0) ? (
        <p style={{ color: '#8a99a8', fontSize: '0.85rem', margin: 0 }}>No polls yet. Ask the family what's for dinner.</p>
      ) : (
        <>
          {open.map(p => (
            <PollCard key={p.id} poll={p} isChild={isChild} proxyMembers={proxyMembers}
              votingFor={votingFor[p.id] || ''} setVotingFor={(v) => setVotingFor(s => ({ ...s, [p.id]: v }))}
              onVote={(oid) => vote(p, oid)} onClose={() => closePoll(p)} onDelete={() => deletePoll(p)} />
          ))}
          {closed.map(p => (
            <PollCard key={p.id} poll={p} closed isChild={isChild}
              onDelete={() => deletePoll(p)}
              onPlan={p.kind === 'meal_choice' ? () => { setPlanFor(p); setPlanDate(''); } : null} />
          ))}
        </>
      )}

      {/* Create modal */}
      <AppModal open={creating} onClose={() => setCreating(false)} closeOnBackdrop ariaLabel="New poll" maxWidth={420}>
        <div style={{ padding: '1.25rem 1.35rem', fontFamily: FONT }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: NAVY, fontWeight: 800 }}>New meal poll</h3>
          <input value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="What shall we eat?" style={inp} />
          {newOpts.map((o, i) => (
            <input key={i} value={o} onChange={e => setNewOpts(a => a.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} style={{ ...inp, marginTop: 6 }} />
          ))}
          <button onClick={() => setNewOpts(a => [...a, ''])} style={{ marginTop: 6, fontFamily: FONT, fontSize: '0.8rem', background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '0.35rem 0.7rem', cursor: 'pointer', color: '#64748b' }}>+ Add option</button>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button onClick={() => setCreating(false)} style={btnGhost}>Cancel</button>
            <button onClick={createPoll} style={btnPrimary}>Create poll</button>
          </div>
        </div>
      </AppModal>

      {/* Winner -> slot */}
      <AppModal open={!!planFor} onClose={() => setPlanFor(null)} closeOnBackdrop ariaLabel="Add winner to plan" maxWidth={380}>
        {planFor && (
          <div style={{ padding: '1.25rem 1.35rem', fontFamily: FONT }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: NAVY, fontWeight: 800 }}>Add the winner to the plan</h3>
            <p style={{ margin: '0 0 0.85rem', fontSize: '0.85rem', color: '#6b7280' }}>
              “{(planFor.tally.find(t => t.optionId === planFor.winnerOptionId) || {}).label || 'Winner'}”, pick a day and slot.
            </p>
            <input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} style={inp} />
            <select value={planSlot} onChange={e => setPlanSlot(e.target.value)} style={{ ...inp, marginTop: 6 }}>
              {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={() => setPlanFor(null)} style={btnGhost}>Cancel</button>
              <button onClick={addWinnerToPlan} disabled={!planDate} style={{ ...btnPrimary, opacity: planDate ? 1 : 0.5 }}>Add to plan</button>
            </div>
          </div>
        )}
      </AppModal>
    </div>
  );
}

function PollCard({ poll, closed, isChild, proxyMembers = [], votingFor, setVotingFor, onVote, onClose, onDelete, onPlan }) {
  const total = poll.totalVotes || 0;
  return (
    <div style={{ border: '1px solid #eef2f6', borderRadius: 10, padding: '0.7rem 0.8rem', marginBottom: '0.6rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontWeight: 700, color: NAVY, fontSize: '0.92rem' }}>{poll.question}</span>
        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{total} vote{total === 1 ? '' : 's'}{closed ? ' · closed' : ''}</span>
      </div>

      <div style={{ marginTop: '0.5rem' }}>
        {poll.tally.map(t => {
          const pct = total ? Math.round((t.count / total) * 100) : 0;
          const mine = poll.myOptionId === t.optionId;
          const win = closed && poll.winnerOptionId === t.optionId;
          return (
            <div key={t.optionId} style={{ marginBottom: 5 }}>
              <button disabled={closed} onClick={() => onVote && onVote(t.optionId)}
                style={{
                  width: '100%', textAlign: 'left', position: 'relative', overflow: 'hidden',
                  border: `1px solid ${mine || win ? NAVY : '#e5e7eb'}`, borderRadius: 8,
                  background: '#fff', cursor: closed ? 'default' : 'pointer', padding: '0.4rem 0.6rem', fontFamily: FONT,
                }}>
                <span style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: win ? '#dcfce7' : '#eef2ff', zIndex: 0 }} />
                <span style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem', color: NAVY }}>
                  <span>{win ? '🏆 ' : ''}{t.label}{mine ? ' ✓' : ''}</span>
                  <span style={{ color: '#6b7280' }}>{t.count}</span>
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {!closed && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          {!isChild && proxyMembers.length > 0 && (
            <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Voting as:&nbsp;
              <select value={votingFor} onChange={e => setVotingFor(e.target.value)} style={{ fontFamily: FONT, fontSize: '0.78rem', padding: '0.2rem 0.35rem', borderRadius: 6, border: '1px solid #dde3ea' }}>
                <option value="">Me</option>
                {proxyMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
          )}
          {poll.canClose && <button onClick={onClose} style={miniBtn}>Close</button>}
          {poll.canClose && <button onClick={onDelete} style={{ ...miniBtn, color: '#b91c1c' }}>Delete</button>}
        </div>
      )}
      {closed && (
        <div style={{ display: 'flex', gap: 8, marginTop: '0.5rem' }}>
          {onPlan && <button onClick={onPlan} style={{ ...miniBtn, background: '#e4e69b', border: 'none', color: NAVY, fontWeight: 700 }}>Add winner to plan</button>}
          {poll.kind === 'recipe_approval' && <span style={{ fontSize: '0.78rem', color: poll.winnerOptionId === 'yes' ? '#065f46' : '#6b7280' }}>{poll.winnerOptionId === 'yes' ? '✓ Added as a family keeper' : 'Not added, the recipe is still saved as-is'}</span>}
          {poll.canClose && <button onClick={onDelete} style={{ ...miniBtn, color: '#b91c1c' }}>Delete</button>}
        </div>
      )}
    </div>
  );
}

const inp = { width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.7rem', border: '1px solid #dde3ea', borderRadius: 8, fontFamily: FONT, fontSize: '0.9rem' };
const btnGhost = { padding: '0.5rem 1rem', background: 'none', border: '1px solid #dde3ea', borderRadius: 8, cursor: 'pointer', color: '#6b7280', fontFamily: FONT };
const btnPrimary = { padding: '0.5rem 1.1rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: FONT };
const miniBtn = { fontFamily: FONT, fontSize: '0.78rem', padding: '0.3rem 0.6rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', color: '#4b5563' };
